import { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { translations } from './translations';

const LanguageContext = createContext();

const CURRENCY_CONFIG = {
    nl: { currency: 'EUR', symbol: '\u20ac', rate: 1, locale: 'nl-NL' },
    en: { currency: 'GBP', symbol: '\u00a3', rate: 0.85, locale: 'en-GB' },
    de: { currency: 'EUR', symbol: '\u20ac', rate: 1, locale: 'de-DE' },
    fr: { currency: 'EUR', symbol: '\u20ac', rate: 1, locale: 'fr-FR' },
    it: { currency: 'EUR', symbol: '\u20ac', rate: 1, locale: 'it-IT' },
    es: { currency: 'EUR', symbol: '\u20ac', rate: 1, locale: 'es-ES' },
    pt: { currency: 'EUR', symbol: '\u20ac', rate: 1, locale: 'pt-PT' },
    pl: { currency: 'PLN', symbol: 'z\u0142', rate: 4.30, locale: 'pl-PL' },
    sv: { currency: 'SEK', symbol: 'kr', rate: 11.50, locale: 'sv-SE' },
    da: { currency: 'DKK', symbol: 'kr', rate: 7.46, locale: 'da-DK' },
    no: { currency: 'NOK', symbol: 'kr', rate: 11.80, locale: 'nb-NO' },
};

const SUPPORTED_LANGUAGES = [
    { code: 'nl', flag: '\ud83c\uddf3\ud83c\uddf1', name: 'Nederlands' },
    { code: 'en', flag: '\ud83c\uddec\ud83c\udde7', name: 'English' },
    { code: 'de', flag: '\ud83c\udde9\ud83c\uddea', name: 'Deutsch' },
    { code: 'fr', flag: '\ud83c\uddeb\ud83c\uddf7', name: 'Fran\u00e7ais' },
    { code: 'it', flag: '\ud83c\uddee\ud83c\uddf9', name: 'Italiano' },
    { code: 'es', flag: '\ud83c\uddea\ud83c\uddf8', name: 'Espa\u00f1ol' },
    { code: 'pt', flag: '\ud83c\uddf5\ud83c\uddf9', name: 'Portugu\u00eas' },
    { code: 'pl', flag: '\ud83c\uddf5\ud83c\uddf1', name: 'Polski' },
    { code: 'sv', flag: '\ud83c\uddf8\ud83c\uddea', name: 'Svenska' },
    { code: 'da', flag: '\ud83c\udde9\ud83c\uddf0', name: 'Dansk' },
    { code: 'no', flag: '\ud83c\uddf3\ud83c\uddf4', name: 'Norsk' },
];

const SUPPORTED_CODES = SUPPORTED_LANGUAGES.map(l => l.code);

// Map browser locale prefixes to our language codes
const LOCALE_MAP = {
    'nl': 'nl', 'en': 'en', 'de': 'de', 'fr': 'fr',
    'it': 'it', 'es': 'es', 'pt': 'pt', 'pl': 'pl',
    'sv': 'sv', 'da': 'da', 'no': 'no', 'nb': 'no', 'nn': 'no',
};

function isPrerender() {
    if (typeof navigator === 'undefined') return true;
    return /HeadlessChrome|ReactSnap|jsdom|prerender|Googlebot|bingbot/i.test(navigator.userAgent || '');
}

function detectInitialLanguage() {
    // 1. Check URL parameter ?lang=xx (highest priority)
    if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        const langParam = urlParams.get('lang')?.toLowerCase();
        if (langParam && SUPPORTED_CODES.includes(langParam)) {
            return langParam;
        }
    }

    // 2. Default to Dutch for pre-rendering and bots (SEO)
    if (isPrerender()) {
        return 'nl';
    }

    // 3. Check browser locale
    const browserLangs = navigator.languages || [navigator.language || 'nl'];
    for (const lang of browserLangs) {
        const prefix = lang.split('-')[0].toLowerCase();
        if (LOCALE_MAP[prefix]) {
            return LOCALE_MAP[prefix];
        }
    }

    // 4. Default to Dutch
    return 'nl';
}

export function LanguageProvider({ children }) {
    const [language, setLanguage] = useState(detectInitialLanguage);

    // Update URL and HTML lang attribute when language changes
    useEffect(() => {
        const url = new URL(window.location);
        if (language !== 'nl') {
            url.searchParams.set('lang', language);
        } else {
            url.searchParams.delete('lang');
        }
        window.history.replaceState({}, '', url);
        
        // Update HTML lang attribute for SEO
        document.documentElement.lang = CURRENCY_CONFIG[language]?.locale?.split('-')[0] || language;
        
        // Update meta description per language (NL keeps static SEO description from index.html)
        const seoDescriptions = {
            nl: 'Contactloos betalen per douchebeurt met de Adyen S1U2 douchelezer. Bereken direct wat kopen of leasen uw camping of recreatiepark oplevert. Lease v.a. \u20ac39/maand.',
            en: 'Calculate your advantage with contactless shower readers from Recrasolutions. Adyen S1U2 terminals for campsites and holiday parks. Buy or lease.',
            de: 'Berechnen Sie Ihren Vorteil mit kontaktlosen Duschlesern von Recrasolutions. Adyen S1U2 Terminals f\u00fcr Campingpl\u00e4tze und Ferienparks.',
            fr: 'Calculez votre avantage avec les lecteurs de douche sans contact de Recrasolutions. Terminaux Adyen S1U2 pour campings et parcs de vacances.',
            it: 'Calcola il tuo vantaggio con i lettori doccia contactless di Recrasolutions. Terminali Adyen S1U2 per campeggi e parchi vacanze.',
            es: 'Calcule su ventaja con los lectores de ducha sin contacto de Recrasolutions. Terminales Adyen S1U2 para campings y parques vacacionales.',
            pt: 'Calcule a sua vantagem com os leitores de duche sem contacto da Recrasolutions. Terminais Adyen S1U2 para parques de campismo.',
            pl: 'Oblicz swoj\u0105 korzy\u015b\u0107 z bezdotykowych czytnik\u00f3w prysznicowych Recrasolutions. Terminale Adyen S1U2 dla kemping\u00f3w i park\u00f3w rekreacyjnych.',
            sv: 'Ber\u00e4kna din f\u00f6rdel med kontaktl\u00f6sa duschavl\u00e4sare fr\u00e5n Recrasolutions. Adyen S1U2 terminaler f\u00f6r campingar och semesteranl\u00e4ggningar.',
            da: 'Beregn din fordel med kontaktl\u00f8se bruseafl\u00e6sere fra Recrasolutions. Adyen S1U2 terminaler til campingpladser og ferieparker.',
            no: 'Beregn din fordel med kontaktl\u00f8se dusjlesere fra Recrasolutions. Adyen S1U2 terminaler for campingplasser og ferieparker.',
        };
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) metaDesc.content = seoDescriptions[language] || seoDescriptions['nl'];
        
        // Update page title per language (NL keeps static SEO title from index.html)
        const seoTitles = {
            nl: 'Douchelezer kopen of leasen v.a. \u20ac39/mnd | Bereken uw voordeel',
            en: 'Recrasolutions | Contactless Payment for Showers',
            de: 'Recrasolutions | Kontaktloses Bezahlen f\u00fcr Duschen',
            fr: 'Recrasolutions | Paiement Sans Contact pour Douches',
            it: 'Recrasolutions | Pagamento Contactless per Docce',
            es: 'Recrasolutions | Pago Sin Contacto para Duchas',
            pt: 'Recrasolutions | Pagamento Sem Contacto para Duches',
            pl: 'Recrasolutions | P\u0142atno\u015b\u0107 Bezdotykowa za Prysznice',
            sv: 'Recrasolutions | Kontaktl\u00f6s Betalning f\u00f6r Duschar',
            da: 'Recrasolutions | Kontaktl\u00f8s Betaling til Brusere',
            no: 'Recrasolutions | Kontaktl\u00f8s Betaling for Dusjer',
        };
        document.title = seoTitles[language] || seoTitles['nl'];
    }, [language]);

    const t = useCallback((key) => {
        return translations[language]?.[key] || translations['nl'][key] || key;
    }, [language]);

    const currencyConfig = useMemo(() => CURRENCY_CONFIG[language] || CURRENCY_CONFIG['nl'], [language]);

    const formatCurrency = useCallback((eurAmount) => {
        const converted = eurAmount * currencyConfig.rate;
        return new Intl.NumberFormat(currencyConfig.locale, {
            style: 'currency',
            currency: currencyConfig.currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(converted);
    }, [currencyConfig]);

    const currencySymbol = currencyConfig.symbol;

    return (
        <LanguageContext.Provider value={{ 
            language, setLanguage, t, languages: SUPPORTED_LANGUAGES,
            formatCurrency, currencySymbol, currencyConfig
        }}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage() {
    const ctx = useContext(LanguageContext);
    if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
    return ctx;
}
